import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Container,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Grid,
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Phone,
  Edit,
  Delete,
  Search,
  Business,
  History,
  CheckCircle,
  Cancel,
  Schedule,
  PhoneCallback,
  Handshake,
  LocalCafe,
  NotificationsActive,
  WhatsApp,
} from '@mui/icons-material';
import axios from 'axios';

interface CallNote {
  id: string;
  date: string;
  note: string;
  status: string;
  calledBy: string;
}

interface CRMCafe {
  id: string;
  cafeName: string;
  contactPerson: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  status: 'pending' | 'called' | 'interested' | 'rejected' | 'partner';
  notes: CallNote[];
  lastCallDate: string | null;
  nextCallDate: string | null;
  createdAt: string;
  updatedAt: string;
}

const statusConfig = {
  pending: { label: 'Aranacak', color: '#ff9800', icon: <Schedule /> },
  called: { label: 'Arandı', color: '#2196f3', icon: <PhoneCallback /> },
  interested: { label: 'İlgileniyor', color: '#9c27b0', icon: <CheckCircle /> },
  rejected: { label: 'Reddetti', color: '#f44336', icon: <Cancel /> },
  partner: { label: 'Partner Oldu', color: '#4caf50', icon: <Handshake /> },
};

const CITY_DISTRICTS: Record<string, string[]> = {
  'İstanbul': [
    'Adalar', 'Arnavutköy', 'Ataşehir', 'Avcılar', 'Bağcılar', 'Bahçelievler', 'Bakırköy', 'Başakşehir',
    'Bayrampaşa', 'Beşiktaş', 'Beykoz', 'Beylikdüzü', 'Beyoğlu', 'Büyükçekmece', 'Çatalca', 'Çekmeköy',
    'Esenler', 'Esenyurt', 'Eyüpsultan', 'Fatih', 'Gaziosmanpaşa', 'Güngören', 'Kadıköy', 'Kağıthane',
    'Kartal', 'Küçükçekmece', 'Maltepe', 'Pendik', 'Sancaktepe', 'Sarıyer', 'Silivri', 'Sultanbeyli',
    'Sultangazi', 'Şile', 'Şişli', 'Tuzla', 'Ümraniye', 'Üsküdar', 'Zeytinburnu'
  ],
  'Ankara': [
    'Akyurt', 'Altındağ', 'Ayaş', 'Bala', 'Beypazarı', 'Çamlıdere', 'Çankaya', 'Çubuk', 'Elmadağ',
    'Etimesgut', 'Evren', 'Gölbaşı', 'Güdül', 'Haymana', 'Kahramankazan', 'Kalecik', 'Keçiören',
    'Kızılcahamam', 'Mamak', 'Nallıhan', 'Polatlı', 'Pursaklar', 'Sincan', 'Şereflikoçhisar', 'Yenimahalle'
  ],
  'İzmir': [
    'Aliağa', 'Balçova', 'Bayındır', 'Bayraklı', 'Bergama', 'Beydağ', 'Bornova', 'Buca', 'Çeşme',
    'Çiğli', 'Dikili', 'Foça', 'Gaziemir', 'Güzelbahçe', 'Karabağlar', 'Karaburun', 'Karşıyaka',
    'Kemalpaşa', 'Kınık', 'Kiraz', 'Konak', 'Menderes', 'Menemen', 'Narlıdere', 'Ödemiş', 'Seferihisar',
    'Selçuk', 'Tire', 'Torbalı', 'Urla'
  ],
  'Bursa': [
    'Büyükorhan', 'Gemlik', 'Gürsu', 'Harmancık', 'İnegöl', 'İznik', 'Karacabey', 'Keles', 'Kestel',
    'Mudanya', 'Mustafakemalpaşa', 'Nilüfer', 'Orhaneli', 'Orhangazi', 'Osmangazi', 'Yenişehir', 'Yıldırım'
  ],
  'Antalya': [
    'Akseki', 'Aksu', 'Alanya', 'Demre', 'Döşemealtı', 'Elmalı', 'Finike', 'Gazipaşa', 'Gündoğmuş',
    'İbradı', 'Kaş', 'Kemer', 'Kepez', 'Konyaaltı', 'Korkuteli', 'Kumluca', 'Manavgat', 'Muratpaşa', 'Serik'
  ]
};

const CafeCRMPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cafes, setCafes] = useState<CRMCafe[]>([]);
  const [filteredCafes, setFilteredCafes] = useState<CRMCafe[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showScheduledOnly, setShowScheduledOnly] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedCafe, setSelectedCafe] = useState<CRMCafe | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    cafeName: '',
    contactPerson: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    district: '',
    status: 'pending' as CRMCafe['status'],
    note: '',
    nextCallDate: '',
  });

  const [callNote, setCallNote] = useState({
    note: '',
    status: 'called' as CRMCafe['status'],
    nextCallDate: '',
  });

  const statusSteps = ['pending', 'called', 'interested', 'partner'];
  const getActiveStep = (status: string) => statusSteps.indexOf(status);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchCafes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        'https://api.sobcof.com/api/crm/cafes',
        getAuthHeaders()
      );
      if (response.data.success) {
        // Transform backend field names to frontend field names
        const transformedData = response.data.data.map((cafe: any) => ({
          id: cafe.id,
          cafeName: cafe.name || '',
          contactPerson: cafe.contactPerson || '',
          phone: cafe.phone || '',
          email: cafe.email || '',
          address: cafe.address || '',
          city: cafe.city || '',
          status: cafe.status || 'pending',
          notes: cafe.callNotes || [],
          callCount: cafe.callCount || 0,
          lastCallDate: cafe.lastCallDate,
          createdAt: cafe.createdAt,
          updatedAt: cafe.updatedAt,
          district: cafe.district || ''
        }));
        setCafes(transformedData);
        setFilteredCafes(transformedData);
      }
    } catch (error) {
      console.error('Error fetching CRM cafes:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCafes();
  }, [fetchCafes]);

  useEffect(() => {
    let filtered = [...cafes];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(cafe =>
        cafe.cafeName.toLowerCase().includes(query) ||
        cafe.contactPerson.toLowerCase().includes(query) ||
        cafe.phone.includes(query) ||
        cafe.city.toLowerCase().includes(query)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(cafe => cafe.status === statusFilter);
    }

    if (showScheduledOnly) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      filtered = filtered.filter(cafe => {
        if (!cafe.nextCallDate) return false;
        const callDate = new Date(cafe.nextCallDate);
        return callDate <= today;
      });
    }

    setFilteredCafes(filtered);
  }, [searchQuery, statusFilter, showScheduledOnly, cafes]);

  const handleAddCafe = async () => {
    try {
      // Check for duplicates (case-insensitive)
      const isDuplicate = cafes.some(
        (cafe) => cafe.cafeName.toLowerCase() === formData.cafeName.toLowerCase()
      );

      if (isDuplicate) {
        alert(`"${formData.cafeName}" isimli kafe zaten CRM listesinde mevcut!`);
        return;
      }

      // Transform frontend field names to backend field names
      const apiData = {
        name: formData.cafeName,
        phone: formData.phone,
        contactPerson: formData.contactPerson,
        address: formData.address,
        city: formData.city,
        district: formData.district,
        notes: '',
        status: formData.status
      };
      const response = await axios.post(
        'https://api.sobcof.com/api/crm/cafes',
        apiData,
        getAuthHeaders()
      );
      if (response.data.success) {
        fetchCafes();
        setAddDialogOpen(false);
        resetForm();
      }
    } catch (error: any) {
      console.error('Error adding cafe:', error.response?.data?.message || error.message);
      alert('Kafe eklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleUpdateCafe = async () => {
    if (!selectedCafe) return;
    try {
      // Transform frontend field names to backend field names
      const apiData = {
        name: formData.cafeName,
        phone: formData.phone,
        contactPerson: formData.contactPerson,
        address: formData.address,
        city: formData.city,
        district: formData.district,
        status: formData.status
      };
      const response = await axios.put(
        `https://api.sobcof.com/api/crm/cafes/${selectedCafe.id}`,
        apiData,
        getAuthHeaders()
      );
      if (response.data.success) {
        // Eğer not girildiyse otomatik arama kaydı da oluştur
        if (formData.note.trim()) {
          try {
            await axios.post(
              `https://api.sobcof.com/api/crm/cafes/${selectedCafe.id}/call`,
              {
                note: formData.note,
                outcome: formData.status,
                nextCallDate: formData.nextCallDate || null
              },
              getAuthHeaders()
            );
          } catch (noteError) {
            console.error('Error adding automatic call note:', noteError);
          }
        }

        fetchCafes();
        setEditDialogOpen(false);
        resetForm();
      }
    } catch (error: any) {
      console.error('Error updating cafe:', error.response?.data?.message || error.message);
      alert('Kafe güncellenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteCafe = async (id: string) => {
    if (!window.confirm('Bu kafeyi silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(
        `https://api.sobcof.com/api/crm/cafes/${id}`,
        getAuthHeaders()
      );
      fetchCafes();
    } catch (error) {
      console.error('Error deleting cafe:', error);
    }
  };

  const handleWhatsAppMessage = async (cafe: CRMCafe) => {
    const message = `Merhaba ${cafe.cafeName}!\n\nSobCof ekibi olarak kullanıcılarımıza İstanbul’daki en sevilen kafeleri sorduk ve kafeniz sıkça önerilenler arasında yer aldı.\n\nBu nedenle sizi SobCof’ta ilk partner kafelerimiz arasında görmek istedik.\n\nSobCof; gençlerin kafeleri keşfettiği, kampanyaları gördüğü ve etkileşime geçtiği kafe odaklı bir sosyal platform.\n\nSize özel bir iş birliği teklifimiz var, paylaşabilir miyiz?`;

    let cleanedPhone = cafe.phone.replace(/[^0-9]/g, '');
    if (cleanedPhone.startsWith('0')) {
      cleanedPhone = '90' + cleanedPhone.substring(1);
    } else if (!cleanedPhone.startsWith('90')) {
      cleanedPhone = '90' + cleanedPhone;
    }

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://web.whatsapp.com/send?phone=${cleanedPhone}&text=${encodedMessage}`, '_blank');

    // Eğer daha önce WhatsApp notu eklenmemişse otomatik olarak CRM'e kaydet
    const alreadySent = cafe.notes?.some(n => n.note.includes('WhatsApp üzerinden iş birliği teklif mesajı gönderildi'));
    
    if (!alreadySent) {
      try {
        await axios.post(
          `https://api.sobcof.com/api/crm/cafes/${cafe.id}/call`,
          {
            note: 'WhatsApp üzerinden iş birliği teklif mesajı gönderildi.',
            outcome: cafe.status === 'pending' ? 'called' : cafe.status, // Aranacak durumundaysa Arandı'ya çek
            nextCallDate: cafe.nextCallDate || null
          },
          getAuthHeaders()
        );
        fetchCafes();
      } catch (error) {
        console.error('Error logging WhatsApp message note:', error);
      }
    }
  };

  const handleAddCallNote = async () => {
    if (!selectedCafe) return;
    try {
      // Transform to backend format
      const apiData = {
        note: callNote.note,
        outcome: callNote.status, // Backend expects 'outcome' not 'status'
        nextCallDate: callNote.nextCallDate || null
      };
      const response = await axios.post(
        `https://api.sobcof.com/api/crm/cafes/${selectedCafe.id}/call`,
        apiData,
        getAuthHeaders()
      );
      if (response.data.success) {
        fetchCafes();
        setCallDialogOpen(false);
        setCallNote({ note: '', status: 'called', nextCallDate: '' });
      }
    } catch (error: any) {
      console.error('Error adding call note:', error.response?.data?.message || error.message);
      alert('Arama notu eklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      cafeName: '',
      contactPerson: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      district: '',
      status: 'pending',
      note: '',
      nextCallDate: '',
    });
  };

  const openEditDialog = (cafe: CRMCafe) => {
    setSelectedCafe(cafe);
    setFormData({
      cafeName: cafe.cafeName,
      contactPerson: cafe.contactPerson,
      phone: cafe.phone,
      email: cafe.email,
      address: cafe.address,
      city: cafe.city,
      district: (cafe as any).district || '',
      status: cafe.status,
      note: '',
      nextCallDate: '',
    });
    setEditDialogOpen(true);
  };

  const openCallDialog = (cafe: CRMCafe) => {
    setSelectedCafe(cafe);
    setCallNote({ note: '', status: cafe.status, nextCallDate: '' });
    setCallDialogOpen(true);
  };

  const openDetailDialog = (cafe: CRMCafe) => {
    setSelectedCafe(cafe);
    setDetailDialogOpen(true);
  };

  const stats = {
    total: cafes.length,
    pending: cafes.filter(c => c.status === 'pending').length,
    called: cafes.filter(c => c.status === 'called').length,
    interested: cafes.filter(c => c.status === 'interested').length,
    rejected: cafes.filter(c => c.status === 'rejected').length,
    partner: cafes.filter(c => c.status === 'partner').length,
    todayCalls: cafes.filter(c => {
      if (!c.nextCallDate) return false;
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      return new Date(c.nextCallDate) <= today;
    }).length,
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#8B4513' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <AppBar position="static" sx={{ backgroundColor: '#8B4513' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, ml: 2 }}>
            Kafe CRM
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              resetForm();
              setAddDialogOpen(true);
            }}
            sx={{ bgcolor: 'white', color: '#8B4513', '&:hover': { bgcolor: '#f5f5f5' } }}
          >
            Yeni Kafe
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Stats Cards */}
        <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          {Object.entries(statusConfig).map(([key, config]) => (
            <Card
              key={key}
              sx={{
                flex: '1 1 150px',
                cursor: 'pointer',
                border: statusFilter === key ? `2px solid ${config.color}` : 'none',
                transition: 'all 0.2s',
                '&:hover': { transform: 'translateY(-4px)' }
              }}
              onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}
            >
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Avatar sx={{ bgcolor: config.color, mx: 'auto', mb: 1 }}>
                  {config.icon}
                </Avatar>
                <Typography variant="h4" fontWeight="bold">
                  {stats[key as keyof typeof stats]}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {config.label}
                </Typography>
              </CardContent>
            </Card>
          ))}
          <Card sx={{ flex: '1 1 150px' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Avatar sx={{ bgcolor: '#8B4513', mx: 'auto', mb: 1 }}>
                <LocalCafe />
              </Avatar>
              <Typography variant="h4" fontWeight="bold">
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Toplam
              </Typography>
            </CardContent>
          </Card>
          <Card
            sx={{
              flex: '1 1 150px',
              cursor: 'pointer',
              border: showScheduledOnly ? '2px solid #f44336' : 'none',
              bgcolor: showScheduledOnly ? '#fff5f5' : 'white'
            }}
            onClick={() => setShowScheduledOnly(!showScheduledOnly)}
          >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Avatar sx={{ bgcolor: '#f44336', mx: 'auto', mb: 1 }}>
                <NotificationsActive />
              </Avatar>
              <Typography variant="h4" fontWeight="bold" color="#f44336">
                {stats.todayCalls}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bugün Aranacak
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Search and Filter */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                placeholder="Kafe adı, kişi, telefon veya şehir ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ flex: 1, minWidth: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Durum Filtresi</InputLabel>
                <Select
                  value={statusFilter}
                  label="Durum Filtresi"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <MenuItem key={key} value={key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: config.color }} />
                        {config.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </CardContent>
        </Card>

        {/* Cafe Table */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Durum</TableCell>
                  <TableCell>Kafe Adı</TableCell>
                  <TableCell>İletişim Kişisi</TableCell>
                  <TableCell>Telefon</TableCell>
                  <TableCell>Şehir</TableCell>
                  <TableCell>Son Arama</TableCell>
                  <TableCell>Sonraki Arama</TableCell>
                  <TableCell align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCafes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {searchQuery || statusFilter !== 'all'
                          ? 'Arama kriterlerine uygun kafe bulunamadı'
                          : 'Henüz kafe eklenmemiş'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCafes.map((cafe) => (
                    <TableRow
                      key={cafe.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openDetailDialog(cafe)}
                    >
                      <TableCell>
                        <Chip
                          label={statusConfig[cafe.status].label}
                          size="small"
                          sx={{
                            bgcolor: statusConfig[cafe.status].color,
                            color: 'white',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">{cafe.cafeName}</Typography>
                      </TableCell>
                      <TableCell>{cafe.contactPerson}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone fontSize="small" color="action" />
                          {cafe.phone}
                        </Box>
                      </TableCell>
                      <TableCell>{cafe.city}</TableCell>
                      <TableCell>
                        {cafe.lastCallDate
                          ? new Date(cafe.lastCallDate).toLocaleDateString('tr-TR')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {cafe.nextCallDate ? (
                          <Chip
                            icon={new Date(cafe.nextCallDate) < new Date() ? <NotificationsActive fontSize="small" /> : undefined}
                            label={new Date(cafe.nextCallDate).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            size="small"
                            color={new Date(cafe.nextCallDate) < new Date() ? 'error' : 'default'}
                            variant={new Date(cafe.nextCallDate) < new Date() ? 'filled' : 'outlined'}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          sx={{ color: cafe.notes?.some(n => n.note.includes('WhatsApp üzerinden')) ? '#9e9e9e' : '#25D366' }}
                          onClick={() => handleWhatsAppMessage(cafe)}
                          title={cafe.notes?.some(n => n.note.includes('WhatsApp üzerinden')) ? "WhatsApp Mesajı Zaten Gönderildi (Tekrar Gönder)" : "WhatsApp Mesajı Gönder"}
                        >
                          <WhatsApp />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openCallDialog(cafe)}
                          title="Arama Ekle"
                        >
                          <Phone />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(cafe)}
                          title="Düzenle"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteCafe(cafe.id)}
                          title="Sil"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      {/* Add Cafe Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Yeni Kafe Ekle</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Kafe Adı"
              value={formData.cafeName}
              onChange={(e) => setFormData({ ...formData, cafeName: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="İletişim Kişisi"
              value={formData.contactPerson}
              onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
              fullWidth
            />
            <TextField
              label="Telefon"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="E-posta"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Adres"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <FormControl fullWidth>
              <InputLabel>Şehir</InputLabel>
              <Select
                value={formData.city}
                label="Şehir"
                onChange={(e) => setFormData({ ...formData, city: e.target.value, district: '' })}
              >
                {Object.keys(CITY_DISTRICTS).map((city) => (
                  <MenuItem key={city} value={city}>{city}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={!formData.city}>
              <InputLabel>İlçe</InputLabel>
              <Select
                value={formData.district}
                label="İlçe"
                onChange={(e) => setFormData({ ...formData, district: e.target.value })}
              >
                {(CITY_DISTRICTS[formData.city] || []).map((district) => (
                  <MenuItem key={district} value={district}>{district}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Durum</InputLabel>
              <Select
                value={formData.status}
                label="Durum"
                onChange={(e) => setFormData({ ...formData, status: e.target.value as CRMCafe['status'] })}
              >
                {Object.entries(statusConfig).map(([key, config]) => (
                  <MenuItem key={key} value={key}>{config.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Sonraki Arama Zamanı"
              type="datetime-local"
              value={formData.nextCallDate}
              onChange={(e) => setFormData({ ...formData, nextCallDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={handleAddCafe} sx={{ bgcolor: '#8B4513' }}>
            Ekle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Cafe Dialog (Integrated Dashboard) */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #eee', bgcolor: '#f8f9fa' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Kafe Düzenle & Görüşme Paneli</Typography>
            <Chip
              label={statusConfig[formData.status].label}
              sx={{ bgcolor: statusConfig[formData.status].color, color: 'white' }}
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Grid container sx={{ height: '75vh' }}>
            {/* Left Column: Form & New Note */}
            <Grid size={{ xs: 12, md: 7 }} sx={{ p: 3, borderRight: '1px solid #eee', overflowY: 'auto' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography variant="subtitle2" color="primary" sx={{ borderBottom: '1px solid #eee', pb: 1, fontWeight: 'bold' }}>
                  KAFE BİLGİLERİ
                </Typography>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      label="Kafe Adı"
                      value={formData.cafeName}
                      onChange={(e) => setFormData({ ...formData, cafeName: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      label="İletişim Kişisi"
                      value={formData.contactPerson}
                      onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      label="Telefon"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      label="E-posta"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Şehir</InputLabel>
                    <Select
                      value={formData.city}
                      label="Şehir"
                      onChange={(e) => setFormData({ ...formData, city: e.target.value, district: '' })}
                    >
                      {Object.keys(CITY_DISTRICTS).map((city) => (
                        <MenuItem key={city} value={city}>{city}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth disabled={!formData.city}>
                    <InputLabel>İlçe</InputLabel>
                    <Select
                      value={formData.district}
                      label="İlçe"
                      onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                    >
                      {(CITY_DISTRICTS[formData.city] || []).map((district) => (
                        <MenuItem key={district} value={district}>{district}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <TextField
                  label="Adres"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                />

                <Typography variant="subtitle2" color="primary" sx={{ borderBottom: '1px solid #eee', pb: 1, mt: 1, fontWeight: 'bold' }}>
                  GÖRÜŞME KAYDI
                </Typography>

                <FormControl fullWidth>
                  <InputLabel>Yeni Durum</InputLabel>
                  <Select
                    value={formData.status}
                    label="Yeni Durum"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as CRMCafe['status'] })}
                  >
                    {Object.entries(statusConfig).map(([key, config]) => (
                      <MenuItem key={key} value={key}>{config.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Görüşme Notu (Tarihçeye eklenecektir)"
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Görüşme detaylarını buraya yazın..."
                  sx={{ bgcolor: '#fffde7' }}
                />

                <TextField
                  label="Sonraki Arama Zamanı"
                  type="datetime-local"
                  value={formData.nextCallDate}
                  onChange={(e) => setFormData({ ...formData, nextCallDate: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Grid>

            {/* Right Column: History */}
            <Grid size={{ xs: 12, md: 5 }} sx={{ bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #eee', bgcolor: '#eee' }}>
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                  <History fontSize="small" /> ARAMA GEÇMİŞİ
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
                {selectedCafe?.notes && selectedCafe.notes.length > 0 ? (
                  <List disablePadding>
                    {selectedCafe.notes.map((note, index) => (
                      <Card key={note.id || index} sx={{ mb: 2, boxShadow: 'none', border: '1px solid #ddd', borderRadius: 2 }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
                            <Chip
                              label={statusConfig[note.status as keyof typeof statusConfig]?.label || note.status}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                bgcolor: statusConfig[note.status as keyof typeof statusConfig]?.color,
                                color: 'white'
                              }}
                            />
                            <Typography variant="caption" color="text.secondary" fontWeight="medium">
                              {new Date(note.date).toLocaleString('tr-TR')}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap', color: '#333' }}>
                            {note.note}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 10, opacity: 0.5 }}>
                    <History sx={{ fontSize: 48, mb: 1, color: '#ccc' }} />
                    <Typography variant="body2" color="text.secondary">Henüz kayıt bulunmuyor</Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #eee', bgcolor: '#f8f9fa' }}>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">İptal</Button>
          <Button
            variant="contained"
            onClick={handleUpdateCafe}
            sx={{ bgcolor: '#8B4513', px: 4, '&:hover': { bgcolor: '#5d2e0d' } }}
            size="large"
          >
            Değişiklikleri & Notu Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Call Note Dialog */}
      < Dialog open={callDialogOpen} onClose={() => setCallDialogOpen(false)} maxWidth="sm" fullWidth >
        <DialogTitle>
          Arama Kaydı Ekle - {selectedCafe?.cafeName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="info">
              Son arama: {selectedCafe?.lastCallDate
                ? new Date(selectedCafe.lastCallDate).toLocaleString('tr-TR')
                : 'Henüz aranmamış'}
            </Alert>
            <TextField
              label="Görüşme Notu"
              value={callNote.note}
              onChange={(e) => setCallNote({ ...callNote, note: e.target.value })}
              fullWidth
              multiline
              rows={4}
              placeholder="Görüşmede konuşulanları yazın..."
              required
            />
            <FormControl fullWidth>
              <InputLabel>Yeni Durum</InputLabel>
              <Select
                value={callNote.status}
                label="Yeni Durum"
                onChange={(e) => setCallNote({ ...callNote, status: e.target.value as CRMCafe['status'] })}
              >
                {Object.entries(statusConfig).map(([key, config]) => (
                  <MenuItem key={key} value={key}>{config.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Sonraki Arama Zamanı"
              type="datetime-local"
              value={callNote.nextCallDate}
              onChange={(e) => setCallNote({ ...callNote, nextCallDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCallDialogOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={handleAddCallNote} sx={{ bgcolor: '#8B4513' }}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog >

      {/* Cafe Detail Dialog */}
      < Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: statusConfig[selectedCafe?.status || 'pending'].color }}>
              <LocalCafe />
            </Avatar>
            <Box>
              <Typography variant="h6">{selectedCafe?.cafeName}</Typography>
              <Chip
                label={statusConfig[selectedCafe?.status || 'pending'].label}
                size="small"
                sx={{
                  bgcolor: statusConfig[selectedCafe?.status || 'pending'].color,
                  color: 'white',
                }}
              />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 4, mt: 2 }}>
            <Stepper activeStep={getActiveStep(selectedCafe?.status || 'pending')} alternativeLabel>
              {statusSteps.map((status) => (
                <Step key={status}>
                  <StepLabel
                    StepIconProps={{
                      sx: {
                        '&.Mui-active': { color: statusConfig[status as keyof typeof statusConfig]?.color },
                        '&.Mui-completed': { color: statusConfig[status as keyof typeof statusConfig]?.color },
                      }
                    }}
                  >
                    {statusConfig[status as keyof typeof statusConfig]?.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Tabs value={currentTab} onChange={(_, v) => setCurrentTab(v)} sx={{ mb: 2 }}>
            <Tab label="Bilgiler" icon={<Business />} iconPosition="start" />
            <Tab label="Arama Geçmişi" icon={<History />} iconPosition="start" />
          </Tabs>

          {currentTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">İletişim Kişisi</Typography>
                  <Typography>{selectedCafe?.contactPerson || '-'}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">Telefon</Typography>
                  <Typography>{selectedCafe?.phone || '-'}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">E-posta</Typography>
                  <Typography>{selectedCafe?.email || '-'}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">Şehir / İlçe</Typography>
                  <Typography>
                    {selectedCafe?.city || '-'}
                    {(selectedCafe as any)?.district ? ` / ${(selectedCafe as any).district}` : ''}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Adres</Typography>
                <Typography>{selectedCafe?.address || '-'}</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">Son Arama</Typography>
                  <Typography>
                    {selectedCafe?.lastCallDate
                      ? new Date(selectedCafe.lastCallDate).toLocaleString('tr-TR')
                      : '-'}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">Sonraki Arama</Typography>
                  <Typography>
                    {selectedCafe?.nextCallDate
                      ? new Date(selectedCafe.nextCallDate).toLocaleDateString('tr-TR')
                      : '-'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {currentTab === 1 && (
            <Box>
              {selectedCafe?.notes && selectedCafe.notes.length > 0 ? (
                <List>
                  {selectedCafe.notes.map((note, index) => (
                    <React.Fragment key={note.id || index}>
                      <ListItem alignItems="flex-start">
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: statusConfig[note.status as keyof typeof statusConfig]?.color || '#ccc', width: 32, height: 32 }}>
                            <Phone fontSize="small" />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Chip
                                label={statusConfig[note.status as keyof typeof statusConfig]?.label || note.status}
                                size="small"
                                sx={{
                                  bgcolor: statusConfig[note.status as keyof typeof statusConfig]?.color || '#ccc',
                                  color: 'white',
                                }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {new Date(note.date).toLocaleString('tr-TR')}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                              {note.note}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < selectedCafe.notes.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" textAlign="center" py={4}>
                  Henüz arama kaydı yok
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDetailDialogOpen(false)}>Kapat</Button>
          <Button
            variant="outlined"
            startIcon={<WhatsApp />}
            sx={{ 
              color: selectedCafe?.notes?.some(n => n.note.includes('WhatsApp üzerinden')) ? '#9e9e9e' : '#25D366', 
              borderColor: selectedCafe?.notes?.some(n => n.note.includes('WhatsApp üzerinden')) ? '#e0e0e0' : '#25D366', 
              '&:hover': { 
                borderColor: selectedCafe?.notes?.some(n => n.note.includes('WhatsApp üzerinden')) ? '#bdbdbd' : '#128C7E', 
                backgroundColor: selectedCafe?.notes?.some(n => n.note.includes('WhatsApp üzerinden')) ? 'rgba(158, 158, 158, 0.04)' : 'rgba(37, 211, 102, 0.04)' 
              } 
            }}
            onClick={() => {
              if (selectedCafe) handleWhatsAppMessage(selectedCafe);
            }}
          >
            {selectedCafe?.notes?.some(n => n.note.includes('WhatsApp üzerinden')) ? 'Tekrar WP Gönder' : 'WhatsApp'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Phone />}
            onClick={() => {
              setDetailDialogOpen(false);
              openCallDialog(selectedCafe!);
            }}
          >
            Arama Ekle
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => {
              setDetailDialogOpen(false);
              openEditDialog(selectedCafe!);
            }}
            sx={{ bgcolor: '#8B4513' }}
          >
            Düzenle
          </Button>
        </DialogActions>
      </Dialog >
    </Box >
  );
};

export default CafeCRMPage;
